import React, { useEffect, useState } from 'react';
import { Box, Button, Card, Checkbox, FormControlLabel, Grid, Typography } from '@material-ui/core';
import GridContainer from '@jumbo/components/GridContainer';
import AppTextInput from '@jumbo/components/Common/formElements/AppTextInput';
import makeStyles from '@material-ui/core/styles/makeStyles';
import CmtImage from '@coremat/CmtImage';
import UploadMovieDropZone from './UploadMovieDropZone';
import { useDispatch, useSelector } from 'react-redux';
import { getConvertableStatus, rqToConvertMovie, rqToGetCompanyToken, rqToGetFileID, uploadDubbed, uploadSubtitle } from 'redux/actions/Convert';
import Resumable from 'resumablejs';
import { qulityIdentifier } from '@jumbo/utils/commonHelper';
import { green } from '@material-ui/core/colors';
import CircularProgressWithLabel from './CircularPercentage';
import { useHistory } from 'react-router';
import Swal from 'sweetalert2';
import UploadSubtitleDropZone from './UploadSubtitleDropZone';
import UploadDubbedDropZone from './UploadDubbedDropZone';
import clsx from 'clsx';
import { fetchError, fetchSuccess } from 'redux/actions';
import SnackUploadStatus from './SnackUploadStatus';

const CHUNK_SIZE = 25; // MB

const useStyles = makeStyles(theme => ({
  cardRoot: {
    padding: 20,
    backgroundColor: '#EFF1FB4D',
  },
  movieInfo: {
    position: 'relative',
    right: 0,
    marginLeft: 'auto',
    marginTop: 40,
    marginBottom: 20,
    maxWidth: 380,
  },
  qualityCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: 20,
    backgroundColor: '#C0C5F033',
  },
  buttonProgress: {
    color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -6,
    marginLeft: -15,
  },
  wrapper: {
    margin: theme.spacing(1),
    position: 'relative',
  },
}));

const qualityMap = {
  lq: '_480p',
  hd: '_720p',
  fullHd: '_1080p',
  bluray: '_bluray',
  fullHq: '_1080hq',
  hls: '_hls',
};

const UploadMovie = ({ movieInfo }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();

  const { fileProps, dubbedFileProps, subtitleFileProps, companyAccessToken, fileID, availableQualities } = useSelector(({ convert }) => convert);
  const [resumable, setResumable] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [startCheckStatus, setStartCheckStatus] = useState(false);

  const [title, setTitle] = useState(fileProps?.name || '');
  const [description, setDescription] = useState(fileProps?.name || '');
  const [qualities, setQualities] = useState(null);
  const [subtitled, setSubtitled] = useState(false);
  const [dubbed, setDubbed] = useState(false);
  const [free, setFree] = useState(false);
  const [publish, setPublish] = useState(false);

  const [lq, setLq] = useState(false);
  const [hd, setHd] = useState(false);
  const [fullHd, setFullHd] = useState(false);
  const [bluray, setBluray] = useState(false);
  const [hls, setHls] = useState(false);
  const [fullHq, setFullHq] = useState(false);

  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadSubtitleLoading, setUploadSubtitleLoading] = useState(false);
  const [uploadDubbedLoading, setUploadDubbedLoading] = useState(false);
  const [allowUpload, setAllowUpload] = useState(false);

  const [openVideoSnack, setOpenVideoSnack] = useState(false);

  useEffect(() => {
    if (fileProps) {
      dispatch(rqToGetCompanyToken(movieInfo.company_id));
    }
  }, [dispatch, fileProps, movieInfo]);

  useEffect(() => {
    const params = {
      title: title !== '' ? title : fileProps?.name,
      description: description !== '' ? description : fileProps?.name,
      companyAccessToken: companyAccessToken?.access_token,
    };
    if (companyAccessToken) {
      dispatch(rqToGetFileID(params));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, companyAccessToken, title, description, fileProps?.name]);

  const handleSubmit = () => {
    const selectedQualities = [];

    if (lq) selectedQualities.push(qualityMap.lq);
    if (hd) selectedQualities.push(qualityMap.hd);
    if (fullHd) selectedQualities.push(qualityMap.fullHd);
    if (fullHq) selectedQualities.push(qualityMap.fullHq);
    if (hls) selectedQualities.push(qualityMap.hls);
    if (bluray) selectedQualities.push(qualityMap.bluray);

    const params = {
      movie_id: movieInfo.id,
      access_token: companyAccessToken?.access_token,
      file_id: fileID?.authorizedToken,
      dubbed,
      dubbed_attached: dubbedFileProps ? true : false,
      subtitle: subtitled,
      subtitle_attached: subtitleFileProps ? true : false,
      free,
      allow_publish: publish,
      qualities: selectedQualities,
    };
    Swal.fire({
      title: `آیا از صحت اطلاعات برای تبدیل فیلم ${movieInfo?.name} مطمعن هستید؟`,
      icon: 'info',
      iconHtml: '؟',
      confirmButtonColor: '#3f51b5',
      cancelButtonColor: '#d33',
      confirmButtonText: 'تبدیل',
      cancelButtonText: 'لغو',
      showCancelButton: true,
      showCloseButton: true,
      preConfirm: () => dispatch(rqToConvertMovie(params, history)),
    });
  };

  useEffect(() => {
    const config = {
      target: 'https://uploader.tapture.ir/elitland/resumable/chunks',
      query: { authToken: fileID?.authorizedToken },
      chunkSize: CHUNK_SIZE * 1024 * 1024,
      simultaneousUploads: 5,
      testTarget: 'https://uploader.tapture.ir/resumable/exists',
      fileType: ['mp4'],
    };

    const r = new Resumable(config);
    setResumable(r);

    // Configure event listeners
    r.on('fileAdded', file => {
      r.upload();
      // Reset the progress when a new file is added
      setUploadProgress(0);
    });

    r.on('fileProgress', file => {
      const progressPercentage = (file.progress() * 100).toFixed(2);
      setUploadProgress(progressPercentage);
      if (progressPercentage > 99) {
        setStartCheckStatus(true);
      }
    });

    r.on('fileSuccess', (file, message) => {
      const response = JSON.parse(message);
      if (response?.data) {
        setUploadLoading(false);
        setOpenVideoSnack(true)
      }
    });

    r.on('fileError', (file, message) => {
      // eslint-disable-next-line no-console
      console.log(`خطا در آپلود فایل: ${message}`);
      dispatch(fetchError(`خطا در آپلود فایل: ${message}`));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileID?.authorizedToken]);

  useEffect(() => {
    let intervalId;

    if (startCheckStatus && availableQualities?.length < 1) {
      intervalId = setInterval(() => {
        dispatch(getConvertableStatus(fileID?.authorizedToken));
      }, 10000);
    }

    if (availableQualities?.length > 0) {
      const qualities = availableQualities.map(quality => qulityIdentifier(quality));
      setQualities(qualities);
      dispatch(fetchSuccess("آماده تبدیل، بارگذاری دوبله و زیرنویس"));
      setAllowUpload(true);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableQualities, dispatch, fileID?.authorizedToken, startCheckStatus]);

  const handleUpload = () => {
    if (resumable && fileProps && fileID?.authorizedToken) {
      resumable.addFile(fileProps);
      setUploadLoading(true);
    }
  };

  const handleUploadDubbed = () => {
    if (fileID?.authorizedToken && dubbedFileProps) {
      dispatch(uploadDubbed({ dubbedFileData: dubbedFileProps, contentId: fileID?.authorizedToken, setUploadDubbedLoading }))
      setUploadDubbedLoading(true)
    }
  }

  const handleUploadSubtitle = () => {
    if (fileID?.authorizedToken && subtitleFileProps) {
      dispatch(uploadSubtitle({ subtitleFileData: subtitleFileProps, contentId: fileID?.authorizedToken, setUploadSubtitleLoading }))
      setUploadSubtitleLoading(true)
    }
  }

  useEffect(() => {
    if (subtitleFileProps) {
      setSubtitled(true)
    }
  }, [subtitleFileProps])

  useEffect(() => {
    if (dubbedFileProps) {
      setDubbed(true)
    }
  }, [dubbedFileProps])

  return (
    <React.Fragment>
      <GridContainer>
        <Grid item xs={12} sm={6}>
          <div style={{ display: 'flex', flexDirection: "column", height: "100%", justifyContent: "space-between" }}>
            <div>
              <CmtImage src={movieInfo?.posters?.large} />
              <Card className={clsx(classes.cardRoot, classes.movieInfo)}>
                <Typography variant="h5">نام فیلم:</Typography>
                <Typography variant="p">{movieInfo?.name}</Typography>
                <Typography variant="h5">نام صاحب اثر:</Typography>
                <Typography variant="p">{movieInfo?.company_name}</Typography>
              </Card>
            </div>
            <div>
              <AppTextInput
                fullWidth
                style={{ marginBottom: 20 }}
                type="text"
                variant="outlined"
                label="عنوان فایل"
                value={title !== '' ? title : fileProps?.name}
                onChange={e => setTitle(e.target.value)}
              />
              <AppTextInput
                multiline
                rows={4}
                fullWidth
                type="text"
                variant="outlined"
                label="توضیحات"
                value={description}
                onChange={e => setDescription(e.target.value)}
              />
            </div>
          </div>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card className={classes.cardRoot} style={{ marginBottom: 10 }}>
            <UploadMovieDropZone />
            {fileProps ? (
              <AppTextInput
                fullWidth
                type="text"
                variant="outlined"
                value={fileProps.name}
                style={{ marginTop: 20 }}
                disabled
              />
            ) : null}
            <div className={classes.wrapper}>
              <Button
                disabled={(!companyAccessToken && !fileID) || uploadLoading}
                variant="contained"
                color="primary"
                onClick={handleUpload}
                fullWidth
                style={{ marginTop: 20 }}>
                {uploadLoading ? 'درحال آپلود...' : 'آپلود ویدئو'}
              </Button>
              {uploadLoading && (
                <CircularProgressWithLabel value={uploadProgress} size={32} className={classes.buttonProgress} />
              )}
            </div>
          </Card>
          <Card className={classes.cardRoot} style={{ marginBottom: 10 }}>
            <UploadSubtitleDropZone disabled={(fileID?.authorizedToken && allowUpload) ? false : true} />
            {subtitleFileProps ? (
              <AppTextInput
                fullWidth
                type="text"
                variant="outlined"
                value={subtitleFileProps.name}
                style={{ marginTop: 20 }}
                disabled
              />
            ) : null}
            <div className={classes.wrapper}>
              <Button
                disabled={(!fileID || !subtitleFileProps) || uploadSubtitleLoading}
                variant="contained"
                color="primary"
                onClick={handleUploadSubtitle}
                fullWidth
                style={{ marginTop: 20 }}>
                {uploadSubtitleLoading ? 'درحال آپلود...' : 'آپلود فایل زیرنویس'}
              </Button>
            </div>
          </Card>
          <Card className={classes.cardRoot}>
            <UploadDubbedDropZone disabled={(fileID?.authorizedToken && allowUpload) ? false : true} />
            {dubbedFileProps ? (
              <AppTextInput
                fullWidth
                type="text"
                variant="outlined"
                value={dubbedFileProps.name}
                style={{ marginTop: 20 }}
                disabled
              />
            ) : null}
            <div className={classes.wrapper}>
              <Button
                disabled={(!fileID || !dubbedFileProps) || uploadDubbedLoading}
                variant="contained"
                color="primary"
                onClick={handleUploadDubbed}
                fullWidth
                style={{ marginTop: 20 }}>
                {uploadDubbedLoading ? 'درحال آپلود...' : 'آپلود فایل دوبله'}
              </Button>
            </div>
          </Card>
        </Grid>
      </GridContainer>
      <GridContainer>
        <Grid item xs={6} sm={3}>
          <FormControlLabel
            control={
              <Checkbox
                checked={subtitled}
                inputProps={{ 'aria-label': 'controlled' }}
                onChange={event => setSubtitled(event.target.checked)}
              />
            }
            label="زیرنویس فارسی"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <FormControlLabel
            control={
              <Checkbox
                checked={dubbed}
                inputProps={{ 'aria-label': 'controlled' }}
                onChange={event => setDubbed(event.target.checked)}
              />
            }
            label="دوبله فارسی"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <FormControlLabel
            control={
              <Checkbox
                checked={free}
                inputProps={{ 'aria-label': 'controlled' }}
                onChange={event => setFree(event.target.checked)}
              />
            }
            label="رایگان"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <FormControlLabel
            control={
              <Checkbox
                checked={publish}
                inputProps={{ 'aria-label': 'controlled' }}
                onChange={event => setPublish(event.target.checked)}
              />
            }
            label="منتشر شود"
          />
        </Grid>
      </GridContainer>
      <GridContainer>
        <Grid item xs={12} sm={12}>
          <Card className={classes.qualityCard}>
            <div>
              <FormControlLabel
                control={
                  <Checkbox
                    disabled={!qualities?.some(quality => quality === '360')}
                    checked={lq}
                    inputProps={{ 'aria-label': 'controlled' }}
                    onChange={() => setLq(!lq)}
                  />
                }
                label="۴۸۰"
                style={{ marginLeft: 50 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={hd}
                    disabled={!qualities?.some(quality => quality === '480')}
                    inputProps={{ 'aria-label': 'controlled' }}
                    onChange={() => setHd(!hd)}
                  />
                }
                label="۷۲۰"
                style={{ marginLeft: 50 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={fullHd}
                    disabled={!qualities?.some(quality => quality === '720')}
                    inputProps={{ 'aria-label': 'controlled' }}
                    onChange={() => setFullHd(!fullHd)}
                  />
                }
                label="۱۰۸۰"
                style={{ marginLeft: 50 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    disabled={!qualities?.some(quality => quality === '720')}
                    checked={bluray}
                    inputProps={{ 'aria-label': 'controlled' }}
                    onChange={() => setBluray(!bluray)}
                  />
                }
                label="bluray"
                style={{ marginLeft: 50 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    disabled={!qualities?.some(quality => quality === '360')}
                    checked={hls}
                    inputProps={{ 'aria-label': 'controlled' }}
                    onChange={() => setHls(!hls)}
                  />
                }
                label="HLS"
                style={{ marginLeft: 50 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    disabled={!qualities?.some(quality => quality === '720')}
                    checked={fullHq}
                    inputProps={{ 'aria-label': 'controlled' }}
                    onChange={() => setFullHq(!fullHq)}
                  />
                }
                label="۱۰۸۰hq"
                style={{ marginLeft: 50 }}
              />
            </div>
            <Box display="flex" justifyContent="flex-center" mb={4} mt={4}>
              <Box ml={2}>
                <Button
                  disabled={!lq && !hd && !fullHd && !bluray && !hls && !fullHq}
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}>
                  {qualities && !qualities?.some(quality => quality === '360') ? 'کیفیت نامناسب' : 'تبدیل کردن'}
                </Button>
              </Box>
            </Box>
          </Card>
        </Grid>
      </GridContainer>
      <SnackUploadStatus openVideoSnack={openVideoSnack} setOpenVideoSnack={setOpenVideoSnack} message="ویدئو آپلود شد" />
    </React.Fragment>
  );
};

export default UploadMovie;
